'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Icon from '@/components/ui/AppIcon';
import AboutScene from './AboutScene';

const MobileAboutHero = () => {
    const containerRef = useRef<HTMLElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const descRef = useRef<HTMLParagraphElement>(null);
    const badgeRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

        // Animate the bottom card sliding up
        tl.from(contentRef.current, {
            y: 100,
            opacity: 0,
            duration: 1.0,
            delay: 0.2
        })
            .from(badgeRef.current, {
                y: 20,
                opacity: 0,
                duration: 0.8
            }, '-=0.6')
            .from(titleRef.current, {
                y: 20,
                opacity: 0,
                duration: 0.8
            }, '-=0.6')
            .from(descRef.current, {
                y: 20,
                opacity: 0,
                duration: 0.8
            }, '-=0.6');

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="relative h-[90vh] flex flex-col bg-slate-50 overflow-hidden">
            {/* Top Animation Section (45%) */}
            <div className="h-[45%] relative w-full overflow-hidden bg-slate-50">
                <div className="absolute inset-0 scale-125 translate-y-4">
                    <AboutScene />
                </div>
                {/* Subtle overlay to blend bottom edge */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-50/50 to-transparent pointer-events-none" />
            </div>

            {/* Bottom Content Card (55%) */}
            <div
                ref={contentRef}
                className="h-[55%] bg-white rounded-t-[32px] shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.1)] relative z-10 px-6 pt-10 pb-8 flex flex-col -mt-8"
            >
                {/* Drag Handle Indicator (Visual only) */}
                <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-8 opacity-60" />

                <div className="flex-1 flex flex-col">
                    {/* Badge */}
                    <div ref={badgeRef} className="inline-flex items-center space-x-2 self-start mb-6">
                        <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                        </span>
                        <span className="text-xs font-bold text-indigo-600 uppercase tracking-widest bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
                            Our DNA
                        </span>
                    </div>

                    {/* Headline */}
                    <h1 ref={titleRef} className="text-4xl font-black text-slate-900 leading-[1.1] mb-6 tracking-tight">
                        Where Tech <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-blue-600">
                            Excellence
                        </span> <br />
                        Meets Strategy
                    </h1>

                    {/* Description */}
                    <p ref={descRef} className="text-slate-600 text-lg leading-relaxed font-medium">
                        Founded by specialists who believe brands grow best when engineering and visibility designed together.
                    </p>
                </div>
            </div>
        </section>
    );
};

export default MobileAboutHero;
